import type { ConnectionPhase } from '@parsleyai/ui'
import { collection, type DocumentData, limit, onSnapshot, orderBy, type QuerySnapshot, query } from 'firebase/firestore'
import { createContext, type ReactNode, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type { MealPlanRequest } from '@/types/mealPlan'
import { initialMealPlanRequestsState, mealPlanRequestsReducer } from './mealPlanRequestsReducer'
import { MealPlanRequestsActionTypes, type MealPlanRequestsState } from './types'

export interface MealPlanRequestsContextValue {
  state: MealPlanRequestsState
  /** Live-connection phase for the subscription (auto-resubscribes on drop; banner-ready). */
  phase: ConnectionPhase
  /** Manual resubscribe. */
  retry: () => void
}

export const MealPlanRequestsContext = createContext<MealPlanRequestsContextValue | undefined>(undefined)

interface MealPlanRequestsProviderProps {
  children: ReactNode
}

// Read-only slice: request docs are written by the backend and driven through their lifecycle by the
// onMealPlanRequestCreated listener. Streams just the newest request (orderBy createdAt desc, limit 1) —
// the docs are large and history is irrelevant here.
export function MealPlanRequestsProvider({ children }: MealPlanRequestsProviderProps) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(mealPlanRequestsReducer, initialMealPlanRequestsState)

  // Drop cached requests on sign-out; the subscription itself is disabled by the null key below.
  useEffect(() => {
    if (!uid) dispatch({ type: MealPlanRequestsActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'mealPlanRequests',
    subscribe: (activeUid, { onData, onError }) =>
      onSnapshot(query(collection(db, 'users', activeUid, 'meal_plan_requests'), orderBy('createdAt', 'desc'), limit(1)), onData, onError),
    onData: (snapshot) => {
      const mealPlanRequests = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as MealPlanRequest)
      dispatch({ type: MealPlanRequestsActionTypes.SET, payload: mealPlanRequests })
    }
  })

  return <MealPlanRequestsContext.Provider value={{ state, phase, retry }}>{children}</MealPlanRequestsContext.Provider>
}
